import { HttpInterceptorFn } from '@angular/common/http';
import { API_BASE_URL } from './api-config';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locale.service';

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The backend owns every server-error text and localizes it by the request's `Accept-Language`, so every request to
 * the API carries the language currently selected in the console ('ar' or 'en'). Read per request, so a language
 * switch applies to the very next call. Requests to other origins (i18n files, third parties) are left alone.
 */
export const acceptLanguageInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(API_BASE_URL)) return next(req);
  // The browser's IANA time zone, so the few times the server writes itself (an export cell) read in the viewer's own zone, like
  // every date on screen. Safe to omit when the browser cannot say: the API then uses UTC.
  const timeZone = browserTimeZone();
  const withZone = timeZone && !req.headers.has('X-Time-Zone') ? req.clone({ setHeaders: { 'X-Time-Zone': timeZone } }) : req;
  if (withZone.headers.has('Accept-Language')) return next(withZone);
  // The language LocaleService.apply() put on <html lang>. Not TranslateService itself: its i18n loader goes through HttpClient, so
  // injecting it from an interceptor is a circular dependency (NG0200) that leaves the app blank.
  const current = document.documentElement.lang ?? '';
  const lang = (SUPPORTED_LOCALES as readonly string[]).includes(current)
    ? current
    : DEFAULT_LOCALE;
  return next(withZone.clone({ setHeaders: { 'Accept-Language': lang } }));
};
